export const SHOP_STATUSES = Object.freeze(["draft", "published", "hidden", "archived"]);
export const MEDIA_TYPES = Object.freeze(["image/jpeg", "image/png", "image/webp"]);
export const MAX_MEDIA_BYTES = 8 * 1024 * 1024;
export const ASSET_KINDS = Object.freeze([
  "standard", "vehicle", "property", "component", "furnishing",
  "apparel", "collectible", "equipment",
]);

function roleKey(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function normaliseBuildSlots(value) {
  return (Array.isArray(value) ? value : []).slice(0, 40).map((slot) => ({
    key: roleKey(slot?.key),
    label: String(slot?.label || "").trim(),
    accepts: [...new Set((Array.isArray(slot?.accepts) ? slot.accepts : [])
      .map(roleKey).filter(Boolean))],
    capacity: Math.max(1, Math.min(1000, Number.parseInt(slot?.capacity, 10) || 1)),
  }));
}

export function normaliseSlots(value) {
  const source = Array.isArray(value) ? value : [];
  const byNumber = new Map(source
    .filter((slot) => Number.isInteger(slot?.slotNumber) && slot.slotNumber >= 1 && slot.slotNumber <= 12)
    .map((slot) => [slot.slotNumber, slot]));
  return Array.from({ length: 12 }, (_, index) => {
    const slotNumber = index + 1;
    return byNumber.get(slotNumber) || { slotNumber, state: "empty", product: null };
  });
}

export function pageFilledCount(page) {
  return normaliseSlots(page?.slots).filter((slot) => slot.product).length;
}

export function nextPageNumber(pages) {
  const numbers = (Array.isArray(pages) ? pages : [])
    .map((page) => Number(page?.pageNumber))
    .filter((value) => Number.isInteger(value) && value > 0);
  return (numbers.length ? Math.max(...numbers) : 0) + 1;
}

export function productPayload(form, publish = false) {
  const cost = Number(form.shopCreditCost);
  const specifications = (Array.isArray(form.specifications) ? form.specifications : [])
    .map((row) => ({ label: String(row?.label || "").trim(), value: String(row?.value || "").trim() }))
    .filter((row) => row.label && row.value);
  return {
    name: String(form.name || "").trim(),
    shortText: String(form.shortText || "").trim(),
    shopCreditCost: Number.isInteger(cost) ? cost : 0,
    displayRetailValue: String(form.displayRetailValue || "").trim(),
    description: String(form.description || "").trim(),
    specifications,
    mainMediaId: form.mainMediaId || null,
    galleryMediaIds: Array.isArray(form.galleryMediaIds) ? form.galleryMediaIds.filter(Boolean) : [],
    progressionLevel: Math.max(1, Math.min(5, Number.parseInt(form.progressionLevel, 10) || 1)),
    featured: Boolean(form.featured),
    new: Boolean(form.new),
    premium: Boolean(form.premium),
    assetKind: ASSET_KINDS.includes(form.assetKind) ? form.assetKind : "standard",
    buildDefinition: { slots: normaliseBuildSlots(form.buildDefinition?.slots) },
    installRoles: [...new Set((Array.isArray(form.installRoles) ? form.installRoles : [])
      .map(roleKey).filter(Boolean))],
    status: publish ? "published" : "draft",
  };
}

export function validateProduct(form) {
  const errors = {};
  if (!String(form.name || "").trim()) errors.name = "Enter a product name.";
  if (!String(form.shortText || "").trim()) errors.shortText = "Enter the short card text.";
  if (!form.mainMediaId) errors.mainMediaId = "Choose a main image.";
  const cost = Number(form.shopCreditCost);
  if (!Number.isInteger(cost) || cost <= 0) errors.shopCreditCost = "Enter a positive whole-number Shop Credit cost.";
  const progressionLevel = Number(form.progressionLevel);
  if (!Number.isInteger(progressionLevel) || progressionLevel < 1 || progressionLevel > 5) {
    errors.progressionLevel = "Choose a progression level from 1 to 5.";
  }
  if (!ASSET_KINDS.includes(form.assetKind)) errors.assetKind = "Choose a valid asset type.";
  const installRoles = Array.isArray(form.installRoles) ? form.installRoles : [];
  const cleanRoles = installRoles.map(roleKey).filter(Boolean);
  if (cleanRoles.length !== installRoles.length || new Set(cleanRoles).size !== cleanRoles.length) {
    errors.installRoles = "Install roles must be unique lowercase keys.";
  }
  const slots = Array.isArray(form.buildDefinition?.slots) ? form.buildDefinition.slots : [];
  if (slots.length > 40 || slots.some((slot) => !roleKey(slot?.key) || !String(slot?.label || "").trim()
    || !(Array.isArray(slot?.accepts) && slot.accepts.map(roleKey).filter(Boolean).length))) {
    errors.buildDefinition = "Every build slot needs a unique key, label and at least one accepted install role.";
  } else if (new Set(slots.map((slot) => roleKey(slot.key))).size !== slots.length) {
    errors.buildDefinition = "Build slot keys must be unique.";
  }
  const specifications = Array.isArray(form.specifications) ? form.specifications : [];
  if (specifications.some((row) => Boolean(String(row?.label || "").trim()) !== Boolean(String(row?.value || "").trim()))) {
    errors.specifications = "Complete both fields in each specification row, or remove the row.";
  }
  return errors;
}

export function blankProduct(seed = {}) {
  return {
    id: seed.id || null,
    name: seed.name || "",
    shortText: seed.shortText || "",
    shopCreditCost: seed.shopCreditCost ?? "",
    displayRetailValue: seed.displayRetailValue || "",
    description: seed.description || "",
    specifications: Array.isArray(seed.specifications) ? seed.specifications.map((row) => ({ ...row })) : [],
    mainMediaId: seed.mainMedia?.id || seed.mainMediaId || null,
    galleryMediaIds: Array.isArray(seed.gallery)
      ? seed.gallery.map((item) => item?.id).filter(Boolean)
      : (seed.galleryMediaIds || []),
    progressionLevel: Number.isInteger(seed.progressionLevel) ? seed.progressionLevel : 1,
    featured: Boolean(seed.featured),
    new: Boolean(seed.new),
    premium: Boolean(seed.premium),
    assetKind: ASSET_KINDS.includes(seed.assetKind) ? seed.assetKind : "standard",
    buildDefinition: { slots: normaliseBuildSlots(seed.buildDefinition?.slots) },
    installRoles: [...new Set((Array.isArray(seed.installRoles) ? seed.installRoles : [])
      .map(roleKey).filter(Boolean))],
    status: SHOP_STATUSES.includes(seed.status) ? seed.status : "draft",
  };
}
